import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, forwardRef, Input } from "@angular/core";
import { CorpControlTitleComponent } from "../../common/corp-control-title/corp-control-title.component";
import { CorpInputComponent, CorpSpinnerComponent } from "@corporate-tailwind/component";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
  selector: 'corp-input-decimal',
  standalone: true,
  templateUrl: 'corp-input-decimal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, CorpControlTitleComponent, CorpSpinnerComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CorpInputDecimalComponent),
      multi: true,
    },
  ],
})
export class CorpInputDecimalComponent extends CorpInputComponent {
@Input() decimals = 2;
private readonly MAX_VALUE = 100_000_000;

displayValue = '';
  private decimalSeparator = this.getDecimalSeparator();

  // détecte locale
  private getDecimalSeparator(): string {
    return (1.1).toLocaleString().substring(1, 2);
  }

  // filtre saisie clavier
  onKeyPress(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    // si user tape "." et locale = ","
    if (event.key === '.' && this.decimalSeparator === ',') {
      event.preventDefault();

      this.insertAtCursor(input, ',');
      return;
    }

    const allowedChars = ['0','1','2','3','4','5','6','7','8','9', this.decimalSeparator];
    if (!allowedChars.includes(event.key)) {
      event.preventDefault();
    }

    // empêcher plusieurs séparateurs
    if (event.key === this.decimalSeparator && input.value.includes(this.decimalSeparator)) {
      event.preventDefault();
    }

    // limite décimales
    const separatorIndex = input.value.indexOf(this.decimalSeparator);

    if (separatorIndex >= 0) {

      const decimals = input.value.substring(separatorIndex + 1);

      const cursorPosition = input.selectionStart ?? 0;

      const isCursorAfterSeparator =
        cursorPosition > separatorIndex;

      if (
        isCursorAfterSeparator &&
        decimals.length >= this.decimals
      ) {
        event.preventDefault();
      }
    }
  }

  override onInput(event: Event) {

    const input = event.target as HTMLInputElement;

    this.displayValue = input.value;

    // état intermédiaire valide UI
    if (
      this.displayValue === '' ||
      this.displayValue === this.decimalSeparator
    ) {
      return;
    }

    const parsed = this.parseValue(this.displayValue);

    if (parsed === null || isNaN(parsed)) {
      return;
    }

    if (parsed > this.MAX_VALUE) {
      return;
    }

    this.value = parsed;

    this.onChange(parsed);
  }

  override onBlur(event: Event) {
    let num = this.parseValue(this.displayValue);
    // si vide/null => 0
    if (num === null || isNaN(num)) {
      num = 0;
    }

    // limite max
    num = Math.min(num, this.MAX_VALUE);

    // valeur modèle
    this.value = num;
    // valeur affichée
    this.displayValue = num.toLocaleString(undefined, {
      minimumFractionDigits: this.decimals,
      maximumFractionDigits: this.decimals
    });

    // update form
    this.onChange(num);
    this.onTouched();
    this.cdr.markForCheck();
  }

  override writeValue(value: number | null): void {
    this.value = value;

    if (value === null || value === undefined) {
      this.displayValue = '';
    } else {
      this.displayValue = value.toLocaleString(undefined, {
        minimumFractionDigits: this.decimals,
        maximumFractionDigits: this.decimals
      });
    }

    this.cdr.markForCheck();
  }

  // convert string → number
  private parseValue(value: string): number | null {
      if (!value) return null;

      const normalized = value
        .replace(/[\s\u00A0]/g, '') // espace normal + espace insécable
        .replace(this.decimalSeparator, '.');
      
        return Number(normalized);
  }

  private insertAtCursor(input: HTMLInputElement, value: string) {

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;

    const currentValue = input.value;

    const newValue =
      currentValue.substring(0, start) +
      value +
      currentValue.substring(end);

    input.value = newValue;
    this.displayValue = newValue;

    queueMicrotask(() => {
      const newPos = start + value.length;
      input.setSelectionRange(newPos, newPos);
    });
  }

}